// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState } from 'react';
import { Modal, TextInput, StatusBanner } from '@loa/ui-components';
import type { Relationship, RelationshipUpdateDTO } from '../types/relationship';

type RelationshipEditModalProps = Readonly<{
  relationship: Relationship;
  open: boolean;
  onClose: () => void;
  onSave: (data: RelationshipUpdateDTO) => Promise<void>;
  onArchive?: () => void;
}>;

export function RelationshipEditModal({ relationship, open, onClose, onSave, onArchive }: RelationshipEditModalProps) {
  const [formData, setFormData] = useState({
    relationshipName: relationship.relationshipName || relationship.profileName || '',
    crmRelationshipNumber: relationship.crmRelationshipNumber || '',
  });
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      setError(null);

      if (!formData.relationshipName || formData.relationshipName.trim() === '') {
        setError('Relationship name is required');
        setSubmitting(false);
        return;
      }

      await onSave({
        relationshipName: formData.relationshipName,
        crmRelationshipNumber: formData.crmRelationshipNumber,
      });
    } catch (err) {
      const errorMessage =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : 'Failed to update relationship';
      setError(errorMessage || 'Failed to update relationship');
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      relationshipName: relationship.relationshipName || relationship.profileName || '',
      crmRelationshipNumber: relationship.crmRelationshipNumber || '',
    });
    setError(null);
    onClose();
  };

  return (
    <Modal open={open} onClose={handleCancel} size="medium">
      <Modal.Header icon="edit" title="Edit Relationship" onClose={handleCancel} />
      <Modal.Content>
        {error && (
          <StatusBanner
            variant="error"
            message={error}
            visible
            position="inline"
            dismissible={false}
          />
        )}

        <div className="flex flex-col gap-xl">
          <TextInput
            label="Relationship Name"
            placeholder="e.g., Smith Family, ABC Corporation"
            value={formData.relationshipName}
            onChange={(e) =>
              setFormData({ ...formData, relationshipName: e.target.value })
            }
            maxLength={255}
            autoFocus
            required
            fluid
          />

          <TextInput
            label="CRM Relationship Number"
            placeholder="e.g., ACC-123456 (optional)"
            value={formData.crmRelationshipNumber}
            onChange={(e) =>
              setFormData({ ...formData, crmRelationshipNumber: e.target.value })
            }
            maxLength={100}
            helperText="External CRM relationship/account identifier for cross-referencing"
            fluid
          />
        </div>

        {onArchive && (
          <div className='mt-xl'>
            <button
              type="button"
              className="bg-transparent border border-error-text rounded-button !text-error-text cursor-pointer px-md h-[48px] !font-sans !text-body-02 !font-medium !leading-normal !capitalize hover:border-[#7F060E] hover:bg-[rgba(254,242,242,0.5)]"
              onClick={onArchive}
              disabled={submitting}
            >
              Archive This Relationship
            </button>
          </div>
        )}
      </Modal.Content>
      <Modal.Actions
        secondaryAction={{ label: 'Cancel', onClick: handleCancel, disabled: submitting }}
        primaryAction={{ label: 'Update Relationship', onClick: () => { void handleSubmit(); }, loading: submitting }}
      />
    </Modal>
  );
}
